import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, ChevronDown, LogOut, Settings, Menu, X as XIcon, Shield, X, SquarePlay } from 'lucide-react'
import CategoryTabs from './CategoryTabs'
import FilterPanel, { FilterToggleButton } from './FilterPanel'
import type { CategoryFilter, SortBy, StatusFilter } from '../../hooks/useCatalogFilters'
import type { Product } from '../../types'
import ShortcutsBar from '../../components/ui/ShortcutsBar'
import { useBannerState } from '../../hooks/useBannerState'

interface Props {
  navVisible?: boolean
  user: { name?: string; email?: string; role?: string } | null
  onLogout: () => void
  search: string
  setSearch: (v: string) => void
  searchRef: React.RefObject<HTMLInputElement | null>
  userMenuOpen: boolean
  setUserMenuOpen: (v: boolean) => void
  mobileMenuOpen: boolean
  setMobileMenuOpen: (v: boolean) => void
  filtersOpen: boolean
  setFiltersOpen: (v: boolean) => void
  activeFilterCount: number
  filtered: Product[]
  selectedCategory: CategoryFilter
  setSelectedCategory: (v: CategoryFilter) => void
  counts: Record<string, number>
  prefersReducedMotion: boolean
  statusFilter: StatusFilter
  setStatusFilter: (v: StatusFilter) => void
  inStockOnly: boolean
  setInStockOnly: (v: boolean) => void
  priceMin: number | ''
  setPriceMin: (v: number | '') => void
  priceMax: number | ''
  setPriceMax: (v: number | '') => void
  sortBy: SortBy
  setSortBy: (v: SortBy) => void
  clearFilters: () => void
  rootCategories: import('../../store/categoryStore').Category[]
  selectedShortcut: string | null
  setSelectedShortcut: (v: string | null) => void
  filteredCount: number
  selectedCategoryName?: string
  onBannerClose?: () => void
}

export default function CatalogTopbar({
  user, onLogout,
  search, setSearch, searchRef,
  userMenuOpen, setUserMenuOpen,
  mobileMenuOpen, setMobileMenuOpen,
  filtersOpen, setFiltersOpen,
  activeFilterCount, filtered,
  selectedCategory, setSelectedCategory, counts,
  prefersReducedMotion,
  statusFilter, setStatusFilter,
  inStockOnly, setInStockOnly,
  priceMin, setPriceMin,
  priceMax, setPriceMax,
  sortBy, setSortBy,
  clearFilters,
  rootCategories,
  selectedShortcut,
  setSelectedShortcut,
  filteredCount,
  selectedCategoryName,
  onBannerClose,
}: Props) {
  const navigate = useNavigate()
  const userMenuRef = useRef<HTMLDivElement>(null)

  const { visible, countdown, isPlayBtnVisible, handleClosePermanent, handleReplay } = useBannerState(onBannerClose)

  return (
    <header className="z-50 transition-transform duration-300 ease-[cubic-bezier(.16,1,.3,1)]" aria-label="ניווט ראשי">
      <div className="topbar-glass">
        <div className="max-w-7xl mx-auto px-6 bg-[#FAF7F2]">

          {/* שורה 1 */}
          <div className="flex items-center justify-between h-[60px] gap-4 lg:gap-0">
            <div className="flex items-center gap-2">
              <span className="sm:hidden font-serif text-[1.3rem] font-bold tracking-tight leading-none">
                <span className="text-[#F4845F]">i</span><span className="text-[#2D6A4F]">Market</span>
              </span>
              <span className="hidden sm:inline font-serif text-2xl font-bold tracking-tight leading-none">
                <span className="text-[#F4845F]">i</span><span className="text-[#2D6A4F]">Market</span>
              </span>
              {user?.role === 'admin' ? (
                <nav className="hidden sm:flex gap-2.5" aria-label="תפריט ראשי">
                  <button onClick={() => navigate('/admin')}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium text-[#6B6560] cursor-pointer hover:bg-[#F2EDE4] transition-colors border-none bg-transparent">
                    <Settings className="w-3.5 h-3.5" />ניהול
                  </button>
                </nav>
              ) : (
                <nav className="hidden sm:block w-[82px]" />
              )}

            </div>

            {/* באנר דסקטופ */}
            <AnimatePresence>
              {visible && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 165 }} exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.775 }}
                  className="hidden sm:block shrink-0 w-[400px] h-[165px] rounded-[8px] overflow-hidden relative top-16.5 z-60">
                  <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover"
                    src="https://scpwgmuctwhwhqepbxzd.supabase.co/storage/v1/object/public/product-images/banner-animation-video.mp4" />
                  <div className="absolute inset-0 bg-black/30" />
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.4 }}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-3 text-center">
                    <p className="text-white font-serif text-[17px] font-bold leading-snug drop-shadow">ירקות ופירות טריים</p>
                    <p className="text-white/75 text-[13px]">איכות חקלאית מהשדה לעסק שלך</p>
                  </motion.div>
                  <button onClick={handleClosePermanent}
                    className="absolute top-3 left-3 z-20 w-7 h-7 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center border-none cursor-pointer transition-colors"
                    aria-label="סגור באנר">
                    <X className="w-4 h-4 text-white" />
                  </button>
                  {countdown !== null && (
                    <div className="absolute bottom-3 right-3 z-20 w-6 h-6 rounded-full bg-black/40 flex items-center justify-center">
                      <span className="text-white text-[11px] font-bold">{countdown}</span>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2 sm:gap-3 flex-row-reverse">
              {user?.role === 'admin' && (
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="sm:hidden flex items-center justify-center w-9 h-9 rounded-xl bg-[#F2EDE4] hover:bg-[#EAE4DB] transition-colors border-none cursor-pointer shrink-0"
                  aria-label="תפריט" aria-expanded={mobileMenuOpen}>
                  {mobileMenuOpen ? <XIcon className="w-4 h-4 text-[#1A1A1A]" /> : <Menu className="w-4 h-4 text-[#1A1A1A]" />}
                </button>
              )}
              <div className="relative w-full hidden">
                <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur() } }}
                  placeholder="חיפוש.." dir="rtl" aria-label="חיפוש מוצרים"
                  className="w-full mt-1 py-1 pr-10 pl-4 rounded-[10px] border-[2.5px] border-[#F2EDE4] bg-[#FAF7F2] text-[16px] text-[#1A1A1A] placeholder:text-[#6E6660] outline-none transition-all focus:border-[#2D6A4F] focus:bg-white focus:shadow-[0_0_0_3px_rgba(45,106,79,0.10)]" />
                <Search className="absolute right-3 top-3/5 -translate-y-1/2 h-3.5 w-3.5 text-[#A89F97] pointer-events-none" />
              </div>
              <FilterToggleButton mobile isOpen={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} activeFilterCount={activeFilterCount} />
              {user?.role === 'admin' && (
                <div ref={userMenuRef} className="relative" data-user-menu>
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#F2EDE4] hover:bg-[#EAE4DB] transition-colors cursor-pointer border-none text-sm font-medium text-[#1A1A1A]"
                    aria-expanded={userMenuOpen} aria-haspopup="true">
                    <span className="w-5 h-5 rounded-full border border-[#2D6A4F] text-[#2D6A4F] text-xs flex items-center justify-center font-semibold select-none">
                      {user?.name?.charAt(0) ?? '?'}
                    </span>
                    <span className="hidden md:flex">{user?.name}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-[#6B6560] transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div initial={{ opacity: 0, y: -6, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }} transition={{ duration: 0.15 }}
                        className="absolute left-0 top-[calc(100%+8px)] w-48 bg-white rounded-2xl shadow-[0_8px_24px_rgba(44,24,16,0.12)] border border-[#F2EDE4] overflow-hidden z-50" role="menu">
                        <div className="px-3.5 py-3 border-b border-[#F2EDE4]">
                          <p className="text-[11px] text-[#6E6660] mb-0.5">מחובר כ</p>
                          <p className="text-[13px] font-semibold text-[#1A1A1A] truncate">{user?.email}</p>
                          {user?.role === 'admin' && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-md bg-[#D8F3DC] text-[#1B4332] text-[10px] font-semibold">
                              <Shield className="w-3 h-3" />אדמין
                            </span>
                          )}
                        </div>
                        <button onClick={() => { onLogout(); setUserMenuOpen(false) }}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-[#C0392B] hover:bg-[#FFE8E0] transition-colors cursor-pointer border-none bg-transparent font-medium" role="menuitem">
                          <LogOut className="w-4 h-4" />התנתקות
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* שורה 2 */}
          <div className="flex gap-3 py-2 items-center">
            <div className="relative flex-1 max-w-[400px]">
              <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur() } }}
                placeholder="חיפוש.." dir="rtl" aria-label="חיפוש מוצרים"
                className="w-full py-[0.25rem] pr-10 pl-4 rounded-[10px] border-[2.5px] border-[#F2EDE4] bg-[#FAF7F2] text-sm text-[#1A1A1A] placeholder:text-[#6E6660] outline-none transition-all focus:border-[#2D6A4F] focus:bg-white focus:shadow-[0_0_0_3px_rgba(45,106,79,0.10)]" />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A89F97] pointer-events-none" />
            </div>
            <FilterToggleButton isOpen={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} activeFilterCount={activeFilterCount} />
            {search && <span className="text-[13px] text-[#6E6660]" aria-live="polite">{filtered.length} תוצאות</span>}
          </div>

          {/* שורה 3 */}
          <div className="flex gap-4 mt-1.5 pb-2 lg:pb-0 items-start">
            <div className="flex-1 min-w-0">
              <CategoryTabs selectedCategory={selectedCategory} onSelect={setSelectedCategory} counts={counts} rootCategories={rootCategories} />
              <ShortcutsBar selectedShortcut={selectedShortcut} onSelect={setSelectedShortcut} selectedCategory={selectedCategory} />
            </div>
          </div>

          {/* שורה 4 */}
          <div className="hidden sm:flex items-baseline justify-between w-full">
            <div className="flex items-baseline gap-3 pb-1">
              <h1 className="font-serif text-[22px] font-bold text-[#1A1A1A] tracking-tight leading-none">קטלוג מוצרים</h1>
              <p className="text-sm text-[#6E6660]" aria-live="polite">
                {filteredCount} מוצרים
                {selectedCategoryName && ` · ${selectedCategoryName}`}
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center gap-1 mr-2 px-2 py-0.5 rounded-full bg-[#FFF3CD] text-[#856404] text-[10px] font-semibold">
                    {activeFilterCount} פילטרים פעילים
                  </span>
                )}
              </p>
            </div>
            <div className="-mt-1">
              <motion.button
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.15, delay: 0.5 }}
                className={`cursor-pointer ${isPlayBtnVisible ? 'visible' : 'hidden'}`}
                onClick={handleReplay}>
                <SquarePlay className="h-6.5 w-6.5 text-green-primary stroke-1" />
              </motion.button>
            </div>
          </div>

          <FilterPanel isOpen={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} activeFilterCount={activeFilterCount}
            prefersReducedMotion={prefersReducedMotion} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            inStockOnly={inStockOnly} setInStockOnly={setInStockOnly} priceMin={priceMin} setPriceMin={setPriceMin}
            priceMax={priceMax} setPriceMax={setPriceMax} sortBy={sortBy} setSortBy={setSortBy} clearFilters={clearFilters} />
        </div>
      </div>
    </header>
  )
}